import { useEffect, useRef, useState } from 'react';
import { api } from './api.js';

interface Thread { id: string; title: string; last_message_at: string; message_count: string }
interface Msg { id: string; role: 'user' | 'assistant'; content_md: string; model: string | null; created_at: string }
interface Caps { model: string; canRead: string[]; canWrite: string[]; toolsAvailableToModel: number; note: string }

export function Chat(): JSX.Element {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [caps, setCaps] = useState<Caps | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void api<Thread[]>('/chat/threads').then(setThreads).catch(() => undefined);
    void api<Caps>('/chat/capabilities').then(setCaps).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!threadId) { setMessages([]); return; }
    void api<{ messages: Msg[] }>(`/chat/threads/${threadId}`).then((r) => setMessages(r.messages)).catch(() => undefined);
  }, [threadId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  async function send(): Promise<void> {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft(''); setBusy(true); setErr(null);
    const optimistic: Msg = { id: `tmp-${Date.now()}`, role: 'user', content_md: text, model: null, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    try {
      const r = await api<{ threadId: string; message: Msg }>('/chat/send', {
        method: 'POST', body: JSON.stringify({ threadId: threadId ?? undefined, message: text }),
      });
      setThreadId(r.threadId);
      setMessages((m) => [...m, { ...r.message, role: 'assistant' }]);
      void api<Thread[]>('/chat/threads').then(setThreads);
    } catch (e) {
      setErr((e as Error).message);
    } finally { setBusy(false); }
  }

  return (
    <>
      <div className="card">
        <div className="crow">
          <h2 style={{ margin: 0 }}>Ask about your data</h2>
          <button type="button" className="ghost" style={{ margin: 0 }} onClick={() => { setThreadId(null); setMessages([]); }}>
            New
          </button>
        </div>
        {caps && (
          <p className="muted small" style={{ marginTop: 8 }}>
            {caps.model} · reads everything · <strong>writes nothing</strong> · {caps.toolsAvailableToModel} tools.
            {' '}{caps.note}
          </p>
        )}
      </div>

      {threads.length > 0 && (
        <div className="card">
          <h2>Conversations</h2>
          <ul className="threads">
            {threads.map((t) => (
              <li key={t.id}>
                <button type="button" className={`linkish${t.id === threadId ? ' on' : ''}`} onClick={() => setThreadId(t.id)}>
                  {t.title}
                </button>
                <span className="muted small">{new Date(t.last_message_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card chatbox">
        {messages.length === 0 && !busy && (
          <div className="muted small">
            <p>It has your profile, targets, food log, weight, scans, training, protocol, bloods, risk score and calendar in front of it. Things worth asking:</p>
            <ul>
              <li>What is my haematocrit actually doing and should I care</li>
              <li>Why did my protein target come out where it did</li>
              <li>What should I be watching for over the next month</li>
              <li>What is worth raising with a doctor, and how do I put it</li>
            </ul>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`msg msg-${m.role}`}>
            <div className="msg-body">{m.content_md}</div>
          </div>
        ))}
        {busy && <div className="msg msg-assistant"><div className="msg-body muted">Thinking…</div></div>}
        {err && <p className="bad">{err}</p>}
        <div ref={endRef} />
      </div>

      <div className="card composer">
        <textarea
          rows={3}
          placeholder="Ask anything about your data…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void send(); }}
        />
        <div className="crow">
          <span className="muted small">Ctrl or Cmd + Enter to send</span>
          <button type="button" disabled={busy || !draft.trim()} onClick={() => void send()}>Send</button>
        </div>
      </div>
    </>
  );
}
